<template>
    <article class="asset-item" :class="{ loading: loading }">
        <div class="asset-item__wrapper">
            <button-back
                v-if="!prePublish"
                class="asset-item__return-back"
                :route-exception="`/my_creations`"
            ></button-back>
            <div :class="!prePublish ? 'asset-item__top-line' : 'no-margin'">
                <div>
                    <h2
                        :title="asset.name"
                        class="asset-item__heading heading heading--lg"
                    >
                        <div class="label">{{ asset.name }}</div>
                    </h2>

                    <div v-if="loading" class="asset-item__text-line"></div>
                    <div v-else class="asset-item__text-line">
                        <div class="author-line">
                            <a
                                :href="
                                    `https://play.ylands.com/profiles/${asset.username}`
                                "
                                target="_blank"
                                rel="noopener noreferrer"
                                :title="
                                    'Click to see all creations from author ' +
                                        asset.username
                                "
                                class="asset-item__author"
                            >
                                {{ asset.username }}
                            </a>

                            <div
                                v-if="asset.verifiedAuthor"
                                title="Verified author"
                                class="asset-item__verified"
                            >
                                <VerifiedIcon
                                    class="asset-item__verified--icon"
                                />

                                <span
                                    class="icon-verified asset-item__verified--text"
                                    >(verified)
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="showRating" class="asset-item__top-line-rating">
                    <RatingAndSubscribers
                        :rating="asset ? asset.rating : 0"
                        :reviews="asset ? asset.reviews : 0"
                        :subscribers="asset ? asset.subscribersCount : 0"
                        :reversed="true"
                        :show-number-of-ratings="true"
                        :hard-yellow="true"
                    />
                </div>
            </div>

            <section class="asset-item__section">
                <p v-if="prePublish" class="publish-note">
                    Please note that published games may require approval by
                    Ylands moderators before they become visible to others.
                </p>
                <p>
                    {{
                        asset.description && asset.description.length > 0
                            ? asset.description
                            : 'No description'
                    }}
                </p>
            </section>

            <AllInOneSlider
                :id="'assetDetail' + asset.id"
                :slides="assetThumbnails"
                :wrapper-class="sliderWrapperClass"
                :with-thumbs="sliderWithThumbs"
            />

            <div class="divider--50"></div>
            <div class="separator"></div>

            <section v-if="!loading" class="asset-item__section">
                <h3
                    class="asset-item__section-heading-details heading heading--lg"
                >
                    Additional Details
                </h3>

                <div
                    v-if="asset.changelog && asset.revision"
                    class="asset-item__section"
                >
                    <div class="additional-info">
                        <h3
                            class="additional-info__section-heading heading heading--sm"
                        >
                            Creation ID:
                        </h3>
                        <h3
                            class="additional-info__section-heading-data heading heading--sm"
                        >
                            {{ asset.metadata.assetGuid }}
                        </h3>
                        <h3
                            class="additional-info__section-heading heading heading--sm"
                        >
                            Asset ID:
                        </h3>
                        <h3
                            class="additional-info__section-heading-data heading heading--sm"
                        >
                            {{ asset.id }}
                        </h3>
                        <h3
                            class="additional-info__section-heading heading heading--sm"
                        >
                            Changelog:
                        </h3>
                        <h3
                            class="additional-info__section-heading-data heading heading--sm"
                        >
                            {{ changelogNumber(asset.revision) }}
                        </h3>
                    </div>
                    <div class="asset-item__changelog">
                        <p v-if="asset.changelog">
                            {{ asset.changelog }}
                        </p>
                    </div>
                </div>
            </section>
        </div>
        <Sidebar
            v-if="!prePublish"
            :asset="asset"
            class="sidebar"
            @report="reportAsset"
            @delete="deleteAsset"
            @hide="hideAsset"
            @hideOnFinish="hideAssetOnFinish"
            @edit="editAsset"
        />

        <Sidebar
            v-if="prePublish"
            :pre-publish="prePublish"
            :asset="asset"
            class="sidebar"
            @report="reportAsset"
            @delete="deleteAsset"
            @hide="hidePrepublishedAsset"
            @hideOnFinish="hideAssetOnFinish"
            @edit="editAsset"
        />

        <YesNoDialog ref="yesNoDialog" />
    </article>
</template>

<script>
import { mapGetters } from 'vuex';

import ProgressLoader from '@/components/common/ProgressBar.vue';
import DateTimeUtil from '@/lib/dateTimeUtil';
import YesNoDialog from '@/components/common/modal/ConfirmationDialog.vue';
import RatingAndSubscribers from '@/components/common/RatingAndSubscribers.vue';
import AllInOneSlider from '@/components/common/AllInOneSlider';
import Sidebar from '@/components/common/Sidebar.vue';
import ButtonBack from '@/components/common/ButtonBack.vue';
import Vue from 'vue';
import Assets from '@/services/assets';
import {
    addErrorNotification,
    addSuccesNotification,
} from '@/lib/notifications';

//SVGS
import VerifiedIcon from 'Svgs/verified_badge.svg?inline';

export default {
    components: {
        RatingAndSubscribers,
        Sidebar,
        AllInOneSlider,
        VerifiedIcon,
        ButtonBack,
        YesNoDialog,
    },
    props: {
        prePublish: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        showWatchAuthor: {
            type: Boolean,
            default: true,
        },
        showSubscription: {
            type: Boolean,
            default: true,
        },
        showRating: {
            type: Boolean,
            default: true,
        },
        showActions: {
            type: Boolean,
            default: true,
        },
        showDates: {
            type: Boolean,
            default: true,
        },
        asset: {
            type: Object,
            default: function() {
                return {};
            },
        },
        sliderWrapperClass: {
            type: String,
            default: null,
        },
        sliderWithThumbs: {
            type: Boolean,
        },
    },
    data() {
        return {
            videos: [],
            screenshots: [],
            ratingEditMode: false,
            assetThumbnails: []
        };
    },
    mounted() { 
        const { media, thumbnail } = this.asset;
        // if (media.length <= 0) { return; }
        this.assetThumbnails = [...this.thumbnailMapper({media, thumbnail})];
    },
    watch: {
        // 'asset.media': {
        //      handler: function (newMedia) {
        //         this.assetThumbnails = [...this.thumbnailMapper(newMedia)]
        //     },
        //     deep: true
        // },
        asset: {
            handler: function (newAsset) {
                        const { media, thumbnail } = newAsset;
                        this.assetThumbnails = [...this.thumbnailMapper({media, thumbnail })]

            },
            deep: true
        }
    },
    computed: {
        ...mapGetters('user', {
            loggedIn: 'isLogged',
            userId: 'getUserId',
        }),
        ...mapGetters('settings', {
            previewSizes: 'previewSizes',
            thumbnailsUrl: 'thumbnailsUrl',
            webpSuppport: 'webp',
        }),
        thumbnailFileName() {
            if (
                this.asset &&
                this.asset.thumbnail &&
                this.asset.thumbnail.data
            ) {
                return this.asset.thumbnail.data.name;
            }
            return 'thumbnail.jpg';
        },
        assetFileName() {
            if (
                this.asset &&
                this.asset.file_info &&
                this.asset.file_info.filename
            ) {
                return this.asset.file_info.filename;
            }
            return 'resource.ycp';
        },

        assetTags() {
            if (this.asset) {
                return this.asset.tags.map(tag => {
                    return tag.id;
                });
            }
            return [];
        },
        myRating() {
            if (this.asset && this.asset.my_rating && !this.ratingEditMode) {
                return this.asset.my_rating.rating;
            }
            return 0;
        },
        belongsToLoggedInUser() {
            return this.asset.user_id === this.userId;
        },
    },
    methods: {
        thumbnailMapper({media, thumbnail = null}) {

            // const videos = this.asset.media.filter(item => item?.data?.type === 'video');

            // if (videos && videos.length > 0) {
            const imagePreviews = media?.map(mediaItem => {

                    if (mediaItem?.data?.type === 'video') {
                        return {
                            data: {
                                id: mediaItem.id,
                                name:
                                    'https://img.youtube.com/vi/' +
                                    mediaItem.id +
                                    '/mqdefault.jpg',
                                type: 'video',
                                params: mediaItem.data.params
                                    ? { start: mediaItem.data.params.start }
                                    : null,
                            }
                        }
                     
                    }
                    return mediaItem
                    
                    
                }) || [];
            // }
            if (imagePreviews.length <= 0 && thumbnail && thumbnail.file_id) {
               imagePreviews.push(thumbnail);
            }
            
            if (imagePreviews.length <= 0) {
                imagePreviews.push('/static/img/asset_thumb_placeholder.jpg');
            }
                    
            return imagePreviews;
        },
        async hidePrepublishedAsset(hidden) {
            let agree = await this.$refs.yesNoDialog.init(
                hidden ? 'Hide this asset?' : 'Show this asset?'
            );
            if (!agree) {
                return;
            }
            try {
                await Assets.patch(
                    this.asset.id,
                    {
                        hidden: hidden,
                    },
                    {}
                );
                Vue.set(this.asset, 'hidden', hidden);
                addSuccesNotification(hidden ? 'Asset hidden' : 'Asset shown');
            } catch (e) {
                console.error(e);
                addErrorNotification('Unable change watch status');
            }
        },
        changelogNumber(changelog) {
            let change = changelog.toString();
            if (change.lastIndexOf('.') >= 0) {
                return change;
            } else {
                return change.concat('.0');
            }
        },
        getcontentTypeVerbose() {
            switch (this.asset.content_type) {
                case 1:
                    return 'mod';
                case 2:
                    return 'creator blueprint';
                case 3:
                    return 'game';
                case 4:
                    return 'game blueprint';
                default:
                    return 'unknown';
            }
        },
        getRatingLabel() {
            if (!this.asset) {
                return;
            }
            let count = 0;
            for (let i = 1; i <= 5; i++) {
                if (
                    this.asset &&
                    this.asset.rating_stats &&
                    this.asset.rating_stats[i]
                ) {
                    count += this.asset.rating_stats[i].count;
                }
            }
            if (count === 0) {
                return 'Not yet rated';
            }
            return (
                'Overall rating: ' +
                this.asset.rating +
                ' by ' +
                count +
                ' users'
            );
        },
        toggleRatingEditMode() {
            this.ratingEditMode = !this.ratingEditMode;
        },
        reportAsset(asset) {
            this.$emit('report', asset);
        },
        editAsset() {
            this.$emit('edit', this.asset);
        },
        deleteAsset() {
            this.$emit('delete', this.asset);
        },
        hideAssetOnFinish(hidden) {
            this.$emit('hide', hidden);
        },
        hideAsset(hidden) {
            this.$emit('hide', hidden);
        },
        getHumanReadableDiffString: function(date) {
            return DateTimeUtil.getHumanReadableDiffString(date);
        },
        getFullDatetimeFormatted: function(date) {
            return DateTimeUtil.getFullDateTime(date);
        },
    },
};
</script>

<style lang="scss" scoped>
.asset-item {
    display: flex;
    justify-content: space-between;
    margin: 1rem auto auto auto;

    @media (max-width: $lg-down) {
        flex-direction: column;
        justify-content: center;
        align-items: center;
        margin: 0 10px;
    }

    &__section-heading-details {
        margin: 2rem auto 2rem 0;
        height: 41px;
        color: #0a294a;
        font-family: $istok-font;
        font-size: 30px;
        font-weight: 700;
        letter-spacing: 0.53px;
        line-height: 53px;
    }

    &__return-back {
        margin: 3rem auto -6rem auto;
    }

    .asset-item__wrapper {
        max-width: 846px;
        width: 100%;
        height: auto;
    }
    .author-line {
        display: inline-flex;
        align-items: center;
        margin-bottom: rem(20);

        .author-name {
            flex: auto;
            word-break: break-all;
        }

        .asset-item__author {
            font-family: $palanquin-font;
            font-size: 20px;
            font-weight: 400;
            letter-spacing: 0.68px;
            color: $blue-main;
        }

        .asset-item__verified {
            &--icon {
                fill: $green-main;
                width: rem(16);
                height: rem(15);
                margin-left: rem(5);
            }

            &--text {
                color: #b2b2b2;
                font-family: $palanquin-font;
                font-size: rem(16);
                font-weight: 400;
                letter-spacing: 0.55px;
                margin-left: rem(11);
            }
        }
    }
}

.additional-info {
    display: inline-grid;
    grid-template-columns: auto auto;
    &__section-heading {
        margin: 0;
        color: #0a294a;
        font-family: $palanquin-font;
        font-size: 18px;
        font-weight: 700;
        letter-spacing: 0.26px;
        line-height: 27px;
    }
    &__section-heading-data {
        margin: 0;
        color: #0a294a;
        font-family: $palanquin-font;
        font-size: 18px;
        font-weight: 400;
        letter-spacing: 0.26px;
        line-height: 27px;
        margin-left: 5px;
    }
}

.asset-item__section {
    padding-bottom: 1.7rem;

    p {
        color: $blue-main;
        font-family: $palanquin-font;
        font-size: 18px;
        font-weight: 400;
        letter-spacing: 0.26px;
        line-height: 27px;
    }
    .asset-item__comments {
        display: flex;
        justify-content: center;

        .asset-item__comments-button {
            color: white;
            background-color: $blue-main;
            padding: 15px 40px;
            border-radius: 6px;
        }
    }
}

.publish-note {
    color: inherit;
    font-style: italic;
}

//
// START
// PLACEHOLDER RELATED STYLES
//

.asset-item.loading {
    .asset-item__heading {
        width: 420px;
        height: 35px;
        background-color: $grey-light;
        color: transparent;
    }

    .asset-item__text-line {
        margin-top: 5px;
        width: 180px;
        height: 15px;
        background-color: $grey-light;
        color: transparent;
    }
}

//
// END
// PLACEHOLDER RELATED STYLES
//

.asset-item__top-line {
    display: flex;
    flex-direction: column;
    margin: 8rem auto auto auto;
    height: auto;

    @media (max-width: $sm-down) {
        display: block;
    }
    .asset-item__top-line-rating {
        flex: 0 0 auto;

        @media (max-width: $sm-down) {
            margin-left: 0;
            margin-top: 1rem;
        }
    }
}

.asset-item__heading {
    margin: 0;
    max-height: 2.2em;
    overflow: hidden;

    @media (max-width: $sm-down) {
        margin: rem(10) 0;
    }
}

.no-margin {
    display: flex;
    flex-direction: column;
    margin: auto;
    height: auto;

    @media (max-width: $sm-down) {
        display: block;
    }
    .asset-item__top-line-rating {
        flex: 0 0 auto;

        @media (max-width: $sm-down) {
            margin-left: 0;
            margin-top: 1rem;
        }
    }
}
</style>
